/**
 * CSK fork of framework CoralDrawer using CskCoralSectionContainer.
 */
import clsx from "clsx";
import { reaction } from "mobx";
import {
  FunctionComponent,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import { graphql, useFragment } from "react-relay";

import { useAnalytics } from "@voxmedia/duet-context/context/analytics-context";
import { useAuth } from "@voxmedia/duet-context/context/auth-context";
import { useConfigContext } from "@voxmedia/duet-context/context/config-context";
import { useCoralDrawerContext } from "@voxmedia/duet-context/context/coral-drawer-context";
import { useLoopNavContext } from "@voxmedia/duet-context/context/loop-nav-context";
import {
  useNotificationsStore,
  useOptionalNotificationsDrawerContext,
} from "@voxmedia/duet-context/context/notifications-context";
import CoralCloseIcon from "@voxmedia/duet-framework/coral/CoralCloseIcon";
import * as styles from "@voxmedia/duet-framework/coral/CoralDrawer.css";
import BellIcon from "@voxmedia/duet-ui/components/icon/BellIcon";
import Drawer from "@voxmedia/duet-ui/components/navigation/Drawer/Drawer";
import { withGracefulErrorBoundary } from "@voxmedia/duet-ui/errors/GracefulErrorBoundary";
import { CskCoralDrawer_contentResource$key } from "src/__generated__/relay/CskCoralDrawer_contentResource.graphql";
import { isCommentsPreviewLocation } from "src/utils/comments-preview";
import getBrowserInfo from "src/utils/get-browser-info";

import type { CoralStreamEmbed } from "./CskCoralLoader";
import CskCoralSectionContainer from "./CskCoralSectionContainer";

interface HistoryState {
  kind: "coral-drawer";
  openedThroughHashChange: boolean;
}

function parseState(state: unknown): HistoryState | null {
  if (
    state &&
    typeof state === "object" &&
    "kind" in state &&
    (state as { kind: unknown }).kind === "coral-drawer"
  ) {
    return state as HistoryState;
  }
  return null;
}

const stateStandard: HistoryState = {
  kind: "coral-drawer",
  openedThroughHashChange: false,
};

const stateOpenedThroughHashChange: HistoryState = {
  kind: "coral-drawer",
  openedThroughHashChange: true,
};

const CoralDrawerNotificationsButton: FunctionComponent = () => {
  const notificationsDrawer = useOptionalNotificationsDrawerContext();
  const { RootStore } = useNotificationsStore();
  const analytics = useAnalytics();
  const [unseenNotificationsDisplayCount, setUnseenNotificationsDisplayCount] =
    useState<number | null>(
      () =>
        RootStore?.notificationsStore.unseenNotificationsDisplayCount ?? null,
    );

  useEffect(() => {
    if (!RootStore) {
      setUnseenNotificationsDisplayCount(null);
      return;
    }

    return reaction(
      () => RootStore.notificationsStore.unseenNotificationsDisplayCount,
      setUnseenNotificationsDisplayCount,
      { fireImmediately: true },
    );
  }, [RootStore]);

  const handleOpenNotifications = useCallback(() => {
    if (!notificationsDrawer || notificationsDrawer.isDrawerOpen) {
      return;
    }
    analytics?.emit({
      event: "notification_click",
    });
    notificationsDrawer.openDrawer();
  }, [analytics, notificationsDrawer]);

  if (!notificationsDrawer) {
    return null;
  }

  return (
    <button
      type="button"
      className={styles.notificationsButton}
      aria-label="Open notifications"
      aria-expanded={notificationsDrawer.isDrawerOpen}
      aria-haspopup="dialog"
      onClick={handleOpenNotifications}
    >
      <BellIcon />
      {unseenNotificationsDisplayCount && (
        <span className={styles.notificationsBadge}>
          {unseenNotificationsDisplayCount}
        </span>
      )}
    </button>
  );
};

function shouldShowComments() {
  return (
    window.location.hash === "#comments" ||
    new URLSearchParams(window.location.search).has("commentID") ||
    isCommentsPreviewLocation()
  );
}

function usePreventScollRestoration() {
  useEffect(() => {
    const onClick = (e: Event) => {
      if (e.target instanceof HTMLElement) {
        let anchor: HTMLElement | null = e.target;
        while (anchor && !(anchor instanceof HTMLAnchorElement)) {
          anchor = anchor.parentElement;
        }
        if (
          anchor &&
          anchor.href === location.origin + location.pathname + "#comments"
        ) {
          if (location.hash && location.hash !== "#comments") {
            const urlWithoutHash = new URL(location.href);
            urlWithoutHash.hash = "";
            window.history.pushState({}, "", urlWithoutHash.toString());
          }
          history.scrollRestoration = "manual";
        }
      }
    };
    window.addEventListener("click", onClick);
    return () => {
      window.removeEventListener("click", onClick);
    };
  });
}

interface Props {
  contentResource: CskCoralDrawer_contentResource$key;
  drawerClassName?: string;
}

const CskCoralDrawer: FunctionComponent<Props> = ({
  drawerClassName,
  ...props
}) => {
  const contentResource = useFragment(
    graphql`
      fragment CskCoralDrawer_contentResource on ContentResourceInterface {
        ...CskCoralSectionContainer_contentResource
      }
    `,
    props.contentResource,
  );

  const {
    isDrawerOpen,
    isDrawerPrerendered,
    isDrawerTransitionDisabled,
    openDrawer,
    closeDrawer,
  } = useCoralDrawerContext();
  const { coralConfig, networkConfig } = useConfigContext();

  const [hasMobileToolbar, setHasMobileToolbar] = useState(false);
  const auth = useAuth();
  const isLoggedIn = auth.user || auth.viewer;

  const { isDrawerOpen: isLoopNavDrawerOpen, closeDrawer: closeLoopNavDrawer } =
    useLoopNavContext();

  usePreventScollRestoration();

  useEffect(() => {
    if (isDrawerOpen && isLoopNavDrawerOpen) {
      closeLoopNavDrawer();
    }
  }, [closeLoopNavDrawer, isDrawerOpen, isLoopNavDrawerOpen]);

  useEffect(() => {
    const browserInfo = getBrowserInfo();
    if (hasMobileToolbar) {
      if (!isLoggedIn) {
        setHasMobileToolbar(false);
      }
      return;
    }
    if (!isLoggedIn) {
      return;
    }
    if (
      window.matchMedia("(max-device-width: 460px)").matches ||
      (browserInfo && (browserInfo.mobile || browserInfo.tablet))
    ) {
      setHasMobileToolbar(true);
    }
  }, [hasMobileToolbar, isLoggedIn]);

  const streamRef = useRef<CoralStreamEmbed | null>(null);

  const onEmbedLoad = useCallback((stream: CoralStreamEmbed) => {
    streamRef.current = stream;
    if (!stream.rendered) {
      stream.render();
    }
  }, []);

  useEffect(() => {
    if (!isDrawerOpen && !isDrawerPrerendered) {
      return;
    }
    const stream = streamRef.current;
    if (!stream || stream.rendered) {
      return;
    }
    stream.render();
  }, [isDrawerOpen, isDrawerPrerendered]);

  // Coral's mobile toolbar mounts outside the drawer (`#coral-shadow-container`).
  // Framework CSS hides it when `bodyDrawerClosed` is on <body>; apply that on
  // initial closed load too (the framework effect only set it in cleanup).
  useEffect(() => {
    if (isDrawerOpen) {
      document.body.classList.add(styles.bodyDrawerOpened);
      document.body.classList.remove(styles.bodyDrawerClosed);
    } else {
      document.body.classList.add(styles.bodyDrawerClosed);
      document.body.classList.remove(styles.bodyDrawerOpened);
    }

    return () => {
      document.body.classList.remove(styles.bodyDrawerOpened);
      document.body.classList.remove(styles.bodyDrawerClosed);
    };
  }, [isDrawerOpen]);

  useEffect(() => {
    const onHashChange = (event: HashChangeEvent) => {
      const newURL = new URL(event.newURL);
      const oldURL = new URL(event.oldURL);
      if (oldURL.hash !== "#comments" && newURL.hash === "#comments") {
        window.history.replaceState(stateOpenedThroughHashChange, "");
      }
    };
    window.addEventListener("hashchange", onHashChange);
    return () => {
      window.removeEventListener("hashchange", onHashChange);
    };
  }, []);

  useEffect(() => {
    if (shouldShowComments()) {
      openDrawer();
    }
  }, [openDrawer]);

  useEffect(() => {
    const browserInfo = getBrowserInfo();
    const usesSwipeTransitions =
      (browserInfo && browserInfo.usesSwipeTransitions) || false;
    const onPopState = () => {
      const state = parseState(history.state);
      if (shouldShowComments() && !isDrawerOpen) {
        openDrawer({
          disableTransition: usesSwipeTransitions && state !== null,
        });
        history.replaceState(state ? state : stateStandard, "");
      }
      if (!shouldShowComments() && isDrawerOpen) {
        closeDrawer({ disableTransition: usesSwipeTransitions });
        history.replaceState(stateStandard, "");
      }
    };
    window.addEventListener("popstate", onPopState);
    return () => {
      window.removeEventListener("popstate", onPopState);
    };
  }, [closeDrawer, isDrawerOpen, openDrawer]);

  const handleOnClose = useCallback(() => {
    const state = parseState(history.state);
    if (state && state.openedThroughHashChange) {
      closeDrawer();
      window.history.back();
    } else {
      const url = new URL(window.location.href);
      url.hash = "";
      url.searchParams.delete("commentID");
      history.replaceState(stateStandard, "");
      window.history.pushState(stateStandard, "", url);
      closeDrawer();
    }
  }, [closeDrawer]);

  if (!coralConfig) {
    return null;
  }

  const closeButtonShape =
    networkConfig.appName === "duet-network-theverge" ? "round" : "square";

  return (
    <Drawer
      drawerClassName={clsx(styles.drawer, drawerClassName)}
      onClose={handleOnClose}
      drawerId="coral-drawer"
      aria-label="Comments Drawer"
      open={isDrawerOpen}
      scrollToTop={false}
      disableTransition={isDrawerTransitionDisabled}
    >
      <div
        className={clsx(
          styles.container,
          hasMobileToolbar && styles.spacingForMobileToolbar,
        )}
      >
        <div className={styles.headerActions}>
          <CoralDrawerNotificationsButton />
          <button
            type="button"
            className={styles.closeButton[closeButtonShape]}
            aria-label="Close"
            onClick={handleOnClose}
          >
            <CoralCloseIcon />
          </button>
        </div>
        <CskCoralSectionContainer
          contentResource={contentResource}
          onEmbedLoad={onEmbedLoad}
          containerClassName={clsx("coral-drawer", drawerClassName)}
          customScrollerID="coral-drawer-scroll"
          coralId="comments-drawer"
        />
      </div>
    </Drawer>
  );
};

export default withGracefulErrorBoundary(CskCoralDrawer, {
  context: "CskCoralDrawerErrorBoundary",
});
